<template>
  <div class="user-email-accounts-panel">
    <div class="user-email-accounts-panel__header">
      <div class="user-email-accounts-panel__header-text">
        <div class="user-email-accounts-panel__section-header">
          <v-icon size="18" class="user-email-accounts-panel__section-icon">
            mdi-email-edit-outline
          </v-icon>
          <span>{{ $t("settings.profile.emailAccounts.title") }}</span>
        </div>
        <p class="text-body-2 text-medium-emphasis mb-0">
          {{ $t("settings.profile.emailAccounts.helper") }}
        </p>
      </div>
      <v-btn color="primary" variant="tonal" @click="openCreateDialog">
        <v-icon start size="16">mdi-plus</v-icon>
        {{ $t("settings.profile.emailAccounts.addIdentity") }}
      </v-btn>
    </div>

    <v-alert
      :type="defaultIdentity ? 'success' : 'info'"
      density="compact"
      class="mt-3"
    >
      {{
        defaultIdentity
          ? $t("settings.profile.emailAccounts.activeSender", {
              email: defaultIdentity.from_email,
            })
          : $t("settings.profile.emailAccounts.companyFallback")
      }}
    </v-alert>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mt-3" />

    <template v-else>
      <v-alert v-if="errorMessage" type="error" density="compact" class="mt-3">
        {{ errorMessage }}
      </v-alert>

      <UserEmailAccountList
        v-if="accounts.length"
        :accounts="accounts"
        :testing-id="testingId"
        @test="handleTest"
        @send-test="openSendTestDialog"
        @edit="openEditDialog"
        @delete="openDeleteDialog"
      />

      <v-alert v-else type="info" density="compact" class="mt-3">
        {{ $t("settings.profile.emailAccounts.empty") }}
      </v-alert>
    </template>

    <UserEmailAccountFormDialog
      :dialog="formDialog"
      :provider-options="providerOptions"
      :transport-options="transportOptions"
      :status-options="statusOptions"
      :auth-options="authOptions"
      @close="closeFormDialog"
      @save="submitForm"
      @provider-change="handleProviderChange"
    />

    <UserEmailAccountSendTestDialog
      :dialog="testEmailDialog"
      @close="closeSendTestDialog"
      @send="submitSendTest"
    />

    <UserEmailAccountDeleteDialog
      :dialog="deleteDialog"
      @close="closeDeleteDialog"
      @confirm="confirmDelete"
      @update:confirmation="deleteDialog.confirmation = $event"
    />

    <v-snackbar v-model="snackbar.show" :timeout="3500" :color="snackbar.color">
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import {
  USER_EMAIL_PROVIDER_PRESETS,
  applyUserEmailProviderPreset,
  buildUserEmailPayload,
  createUserEmailAccount,
  deleteUserEmailAccount,
  fetchUserEmailAccounts,
  normalizeUserEmailAccount,
  sendUserEmailTestEmail,
  testUserEmailAccount,
  updateUserEmailAccount,
} from "@/Utile/userEmailAccounts.mjs";
import {
  createEmptyUserEmailDeleteDialogState,
  createEmptyUserEmailFormDialogState,
  createEmptyUserEmailSendTestDialogState,
  createUserEmailDeleteDialogState,
  createUserEmailFormDialogState,
  createUserEmailSendTestDialogState,
  validateUserEmailFormDialog,
} from "@/components/userAccount/emailAccountDialogState.mjs";
import UserEmailAccountDeleteDialog from "@/components/userAccount/UserEmailAccountDeleteDialog.vue";
import UserEmailAccountFormDialog from "@/components/userAccount/UserEmailAccountFormDialog.vue";
import UserEmailAccountList from "@/components/userAccount/UserEmailAccountList.vue";
import UserEmailAccountSendTestDialog from "@/components/userAccount/UserEmailAccountSendTestDialog.vue";

export default {
  name: "UserEmailAccountsPanel",
  components: {
    UserEmailAccountDeleteDialog,
    UserEmailAccountFormDialog,
    UserEmailAccountList,
    UserEmailAccountSendTestDialog,
  },
  props: {
    userEmail: {
      type: String,
      default: "",
    },
    userName: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      accounts: [],
      loading: false,
      errorMessage: "",
      testingId: null,
      formDialog: createEmptyUserEmailFormDialogState(),
      testEmailDialog: createEmptyUserEmailSendTestDialogState(),
      deleteDialog: createEmptyUserEmailDeleteDialogState(),
      snackbar: {
        show: false,
        message: "",
        color: "info",
      },
    };
  },
  computed: {
    defaultIdentity() {
      const active = this.accounts.filter((account) => account.status !== "disabled");
      return active.find((account) => account.is_default) || active[0] || null;
    },
    providerOptions() {
      return USER_EMAIL_PROVIDER_PRESETS.map((preset) => ({
        title: this.$t(preset.labelKey),
        value: preset.value,
      }));
    },
    transportOptions() {
      return [
        {
          title: this.$t("settings.profile.emailAccounts.transport.companyDefault"),
          value: "company_default",
        },
        {
          title: this.$t("settings.profile.emailAccounts.transport.ownSmtp"),
          value: "own_smtp",
        },
      ];
    },
    statusOptions() {
      return [
        { title: this.$t("settings.profile.emailAccounts.status.active"), value: "active" },
        { title: this.$t("settings.profile.emailAccounts.status.disabled"), value: "disabled" },
      ];
    },
    authOptions() {
      return [
        { title: this.$t("settings.profile.emailAccounts.auth.password"), value: "password" },
        { title: this.$t("settings.profile.emailAccounts.auth.none"), value: "none" },
      ];
    },
  },
  mounted() {
    this.fetchAccounts();
  },
  methods: {
    extractError(error, fallbackKey) {
      return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        this.$t(fallbackKey)
      );
    },
    showSnackbar(message, color = "info") {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    async fetchAccounts() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const { data } = await fetchUserEmailAccounts();
        this.accounts = Array.isArray(data?.accounts)
          ? data.accounts.map(normalizeUserEmailAccount)
          : [];
      } catch (error) {
        this.errorMessage = this.extractError(
          error,
          "settings.profile.emailAccounts.snackbar.loadError"
        );
      } finally {
        this.loading = false;
      }
    },
    openCreateDialog() {
      this.formDialog = createUserEmailFormDialogState({
        userEmail: this.userEmail,
        userName: this.userName,
      });
    },
    openEditDialog(account) {
      this.formDialog = createUserEmailFormDialogState({ mode: "edit", account });
    },
    closeFormDialog() {
      this.formDialog.show = false;
    },
    handleProviderChange(providerKey) {
      this.formDialog.data = applyUserEmailProviderPreset(this.formDialog.data, providerKey);
    },
    validateForm() {
      return validateUserEmailFormDialog(this.formDialog, this.$t.bind(this));
    },
    async submitForm() {
      this.formDialog.error = "";
      if (!this.validateForm()) return;
      this.formDialog.saving = true;
      try {
        const includePassword =
          this.formDialog.data.transportMode === "own_smtp" &&
          this.formDialog.data.authType === "password" &&
          (this.formDialog.mode === "create" || this.formDialog.editPassword);
        const payload = buildUserEmailPayload(this.formDialog.data, { includePassword });
        if (this.formDialog.mode === "create") {
          await createUserEmailAccount(payload);
          this.showSnackbar(this.$t("settings.profile.emailAccounts.snackbar.created"), "success");
        } else {
          await updateUserEmailAccount(this.formDialog.data.id, payload);
          this.showSnackbar(this.$t("settings.profile.emailAccounts.snackbar.updated"), "success");
        }
        this.formDialog.show = false;
        await this.fetchAccounts();
      } catch (error) {
        this.formDialog.error = this.extractError(
          error,
          "settings.profile.emailAccounts.snackbar.saveError"
        );
      } finally {
        this.formDialog.saving = false;
      }
    },
    openSendTestDialog(account) {
      this.testEmailDialog = createUserEmailSendTestDialogState(account, this.userEmail || "");
    },
    closeSendTestDialog() {
      this.testEmailDialog.show = false;
    },
    async submitSendTest() {
      this.testEmailDialog.error = "";
      if (!String(this.testEmailDialog.to || "").trim()) {
        this.testEmailDialog.error = this.$t("settings.profile.emailAccounts.dialog.requiredField");
        return;
      }
      this.testEmailDialog.sending = true;
      try {
        const { data } = await sendUserEmailTestEmail(
          this.testEmailDialog.account.id,
          this.testEmailDialog.to.trim()
        );
        this.showSnackbar(
          this.$t("settings.profile.emailAccounts.snackbar.sendTestOk", {
            messageId: data?.messageId || "-",
          }),
          "success"
        );
        this.testEmailDialog.show = false;
      } catch (error) {
        this.testEmailDialog.error = this.extractError(
          error,
          "settings.profile.emailAccounts.snackbar.sendTestError"
        );
      } finally {
        this.testEmailDialog.sending = false;
      }
    },
    openDeleteDialog(account) {
      this.deleteDialog = createUserEmailDeleteDialogState(account);
    },
    closeDeleteDialog() {
      this.deleteDialog.show = false;
    },
    async confirmDelete() {
      if (this.deleteDialog.confirmation !== "DELETE") return;
      this.deleteDialog.deleting = true;
      this.deleteDialog.error = "";
      try {
        await deleteUserEmailAccount(this.deleteDialog.account.id);
        this.showSnackbar(this.$t("settings.profile.emailAccounts.snackbar.deleted"), "success");
        this.deleteDialog.show = false;
        await this.fetchAccounts();
      } catch (error) {
        this.deleteDialog.error = this.extractError(
          error,
          "settings.profile.emailAccounts.snackbar.deleteError"
        );
      } finally {
        this.deleteDialog.deleting = false;
      }
    },
    async handleTest(account) {
      this.testingId = account.id;
      try {
        const { data } = await testUserEmailAccount(account.id);
        if (data?.ok) {
          this.showSnackbar(this.$t("settings.profile.emailAccounts.snackbar.testOk"), "success");
        } else {
          this.showSnackbar(
            data?.error || this.$t("settings.profile.emailAccounts.snackbar.testError"),
            "error"
          );
        }
        await this.fetchAccounts();
      } catch (error) {
        this.showSnackbar(
          this.extractError(error, "settings.profile.emailAccounts.snackbar.testError"),
          "error"
        );
      } finally {
        this.testingId = null;
      }
    },
  },
};
</script>

<style scoped>
/* Ca și rândul de conturi: panoul trăiește într-o coloană de grid, deci se
   reașază după lățimea proprie, nu după media query-uri pe viewport. */
.user-email-accounts-panel__header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 12px;
  justify-content: space-between;
}

.user-email-accounts-panel__header-text {
  flex: 1 1 220px;
  min-width: 0;
}

.user-email-accounts-panel__section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--app-text-strong);
  margin-bottom: 4px;
}

.user-email-accounts-panel__section-icon {
  color: rgba(var(--v-theme-on-surface), 0.5);
  flex-shrink: 0;
}

</style>
